import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import { Textarea } from "ui/textarea";
import {
  CheckCircle2,
  Eye,
  FileText,
  History,
  ChevronDown,
  ChevronUp,
  Upload,
  Trash2,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { toast } from "sonner";
import { formatUploadedDateTime } from "utils/commonutils";
import { FullScreenLoading } from "components/ui/loader";
import { usePassportOCR } from "hooks/usePassPortOcr";

export interface EmployeeDocument {
  id: string;
  documentId?: number;
  documentMasterId: number;
  name: string;
  acceptedFormats: string;
  maxFileSizeMB: number;
  isRequired: boolean;
  uploadedFile?: File | null;
  uploadedUrl?: string;
  history: {
    id: string;
    action: string;
    historyStatus: string;
    actionAt: string;
    remarks?: string;
  }[];
}

interface DocumentsTabProps {
  documents: EmployeeDocument[];
  setDocuments: React.Dispatch<React.SetStateAction<EmployeeDocument[]>>;
  isViewMode?: boolean;

  employeeId?: number;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUploadDocument?: (documentMasterId: number, file: File) => Promise<any>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDeleteDocument?: (documentId: number, remarks: string) => Promise<any>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPassportOCR?: (ocrData: any) => void;
}

export default function DocumentsTab({
  documents,
  setDocuments,
  isViewMode = false,
  employeeId,
  onUploadDocument,
  onDeleteDocument,
  onPassportOCR,
}: DocumentsTabProps) {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [expandedHistory, setExpandedHistory] = useState<
    Record<string, boolean>
  >({});

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [documentToDelete, setDocumentToDelete] =
    useState<EmployeeDocument | null>(null);

  const [deleteRemark, setDeleteRemark] = useState("");

  const [isUploading, setIsUploading] = useState(false);

  const { scanPassport, ocrLoading } = usePassportOCR();

  const toggleHistory = (docId: string) => {
    setExpandedHistory((prev) => ({
      ...prev,
      [docId]: !prev[docId],
    }));
  };

  const handleUploadClick = (docId: string) => {
    fileInputRefs.current[docId]?.click();
  };

  const handleFileUpload = async (docId: string, file: File | null) => {
    if (!file) return;

    const selectedDoc = documents.find((d) => d.id === docId);

    if (!selectedDoc) return;

    if (!employeeId) {
      toast.error("Please save employee first");
      return;
    }

    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    const allowedFormats = selectedDoc.acceptedFormats
      .split(",")
      .map((f) => f.trim().toLowerCase());

    if (!fileExtension || !allowedFormats.includes(fileExtension)) {
      toast.error(
        `Accepted format: ${selectedDoc.acceptedFormats.toUpperCase()} only`,
      );

      return;
    }

    const maxSizeBytes = selectedDoc.maxFileSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      toast.error(
        `File size must be less than ${selectedDoc.maxFileSizeMB} MB`,
      );

      return;
    }

    try {
      let uploadResponse: {
        action?: string;
        historyStatus?: string;
        documentVersionStatus?: string;
        actionAt?: string;
        uploadedAt?: string;
        remarks?: string;

        versions?: {
          documentVersionRemarks?: string;
          documentVersionStatus?: string;
          histories?: {
            historyStatus?: string;
            actionAt?: string;
          }[];
        }[];
      } | null = null;

      if (onUploadDocument) {
        uploadResponse = await onUploadDocument(
          selectedDoc.documentMasterId,
          file,
        );
      }

      const previewUrl = URL.createObjectURL(file);
      const latestVersion = uploadResponse?.versions?.[0];

      const latestHistory = latestVersion?.histories?.[0];

      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === docId
            ? {
                ...doc,
                uploadedFile: file,
                uploadedUrl: previewUrl,
                history: [
                  {
                    id: Date.now().toString(),

                    action: uploadResponse?.action || "UPLOAD",

                    historyStatus:
                      latestHistory?.historyStatus ||
                      latestVersion?.documentVersionStatus ||
                      "APPROVED",

                    actionAt:
                      latestHistory?.actionAt ||
                      uploadResponse?.actionAt ||
                      uploadResponse?.uploadedAt ||
                      new Date().toISOString(),

                    remarks:
                      latestVersion?.documentVersionRemarks ||
                      uploadResponse?.remarks ||
                      "Approved By System",
                  },

                  ...doc.history,
                ],
              }
            : doc,
        ),
      );

      const isPassportCopy = selectedDoc.name
        .toLowerCase()
        .includes("passport");

      if (isPassportCopy) {
        const ocrData = await scanPassport(file, selectedDoc.name);

        if (ocrData) {
          onPassportOCR?.(ocrData);
        }
      }

      toast.success("Document uploaded successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to upload document");
    }
  };

  const handleDeleteDocument = (docId: string) => {
    const selectedDoc = documents.find((d) => d.id === docId);

    if (!selectedDoc) return;

    setDocumentToDelete(selectedDoc);

    setDeleteRemark("");

    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete?.documentId) {
      toast.error("Document ID not found");
      return;
    }

    if (!deleteRemark.trim()) {
      toast.error("Please enter reason");
      return;
    }

    try {
      setIsUploading(true);

      let deleteResponse: {
        historyStatus?: string;
        actionAt?: string;
        remarks?: string;
      } | null = null;

      if (onDeleteDocument) {
        deleteResponse = await onDeleteDocument(
          documentToDelete.documentId,
          deleteRemark,
        );
      }

      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === documentToDelete.id
            ? {
                ...doc,
                history: [
                  {
                    id: Date.now().toString(),

                    action: "REJECT",

                    historyStatus: deleteResponse?.historyStatus || "REJECTED",

                    actionAt:
                      deleteResponse?.actionAt || new Date().toISOString(),

                    remarks: deleteRemark,
                  },

                  ...doc.history,
                ],
              }
            : doc,
        ),
      );

      toast.success("Document removed successfully");

      setIsDeleteDialogOpen(false);

      setDocumentToDelete(null);

      setDeleteRemark("");
    } catch (error) {
      console.error(error);

      toast.error("Failed to remove document");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <>
        {isUploading && <FullScreenLoading />}
        {ocrLoading && <FullScreenLoading title="Scanning Passport..." />}

        <Card className="border-[#5B9BD5]/20 bg-white shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: "#5B9BD5" }} />

              <CardTitle className="text-gray-900">
                Uploaded Documents
                <span className="text-red-500 ml-1">*</span>
              </CardTitle>
            </div>

            <p className="text-sm text-gray-600 mt-1">
              Required employee documents
            </p>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              {documents.map((doc, index) => {
                const latestHistory = doc.history?.[0];

                const isRejected =
                  latestHistory?.historyStatus?.toUpperCase() === "REJECTED";

                return (
                  <div
                    key={doc.id}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="bg-white hover:bg-gray-50 transition-colors p-4">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                        {/* NUMBER */}
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 flex-shrink-0">
                          {index + 1}
                        </span>

                        {/* NAME */}
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-medium">
                            {doc.name}

                            {doc.isRequired && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </p>

                          <p className="text-xs text-gray-500 mt-1">
                            Accepted format: {doc.acceptedFormats} • Max:{" "}
                            {doc.maxFileSizeMB}MB
                          </p>
                        </div>

                        {/* ACTIONS */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
                          {/* UPLOADED */}
                          <>
                            {/* FILE INPUT */}
                            {!isViewMode && (
                              <input
                                ref={(el) => {
                                  fileInputRefs.current[doc.id] = el;
                                }}
                                type="file"
                                accept={doc.acceptedFormats
                                  .split(",")
                                  .map((f) => `.${f.trim()}`)
                                  .join(",")}
                                className="hidden"
                                onChange={(e) =>
                                  handleFileUpload(
                                    doc.id,
                                    e.target.files?.[0] || null,
                                  )
                                }
                              />
                            )}

                            {/* STATUS */}
                            {doc.uploadedUrl ? (
                              <>
                                <CheckCircle2 className="w-5 h-5 text-green-600" />

                                <span className="text-sm text-gray-700">
                                  Document uploaded
                                </span>

                                {/* VIEW */}
                                <button
                                  className="hover:bg-gray-100 rounded-lg p-1.5 transition-colors"
                                  style={{ color: "#5B9BD5" }}
                                  onClick={() =>
                                    window.open(doc.uploadedUrl, "_blank")
                                  }
                                >
                                  <Eye className="w-4 h-4 cursor-pointer" />
                                </button>

                                {/* HISTORY */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleHistory(doc.id)}
                                  className="text-sm border-gray-300 hover:bg-gray-50 rounded-xl flex items-center gap-1"
                                >
                                  <History className="w-3 h-3" />
                                  History ({doc.history.length})
                                  {expandedHistory[doc.id] ? (
                                    <ChevronUp className="w-3 h-3" />
                                  ) : (
                                    <ChevronDown className="w-3 h-3" />
                                  )}
                                </Button>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">
                                Not uploaded
                              </span>
                            )}

                            {!isViewMode &&
                              (!doc.uploadedUrl || isRejected) && (
                                <>
                                  <input
                                    ref={(el) => {
                                      fileInputRefs.current[doc.id] = el;
                                    }}
                                    type="file"
                                    accept={doc.acceptedFormats
                                      .split(",")
                                      .map((f) => `.${f.trim()}`)
                                      .join(",")}
                                    className="hidden"
                                    onChange={(e) =>
                                      handleFileUpload(
                                        doc.id,
                                        e.target.files?.[0] || null,
                                      )
                                    }
                                  />

                                  <Button
                                    size="sm"
                                    onClick={() => handleUploadClick(doc.id)}
                                    style={{
                                      backgroundColor: "#5B9BD5",
                                    }}
                                    className="text-white text-xs"
                                  >
                                    <Upload className="w-3.5 h-3.5 mr-1" />
                                    Upload
                                  </Button>
                                </>
                              )}

                            {/* DELETE BUTTON */}
                            {!isViewMode && doc.uploadedUrl && !isRejected && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        </div>
                      </div>
                    </div>

                    {/* HISTORY */}
                    {expandedHistory[doc.id] && doc.history.length > 0 && (
                      <div className="bg-gradient-to-r from-blue-50 to-white border-t border-gray-200 p-4">
                        <h4
                          className="text-sm mb-3 font-medium"
                          style={{ color: "#5B9BD5" }}
                        >
                          📋 Document Upload History ({doc.history.length}{" "}
                          records)
                        </h4>

                        <div className="space-y-2">
                          {doc.history.map((item) => (
                            <div
                              key={item.id}
                              className={`p-3 rounded-lg border ${
                                item.historyStatus?.toUpperCase() === "REJECTED"
                                  ? "bg-red-50 border-red-200"
                                  : item.historyStatus?.toUpperCase() ===
                                      "APPROVED"
                                    ? "bg-green-50 border-green-200"
                                    : "bg-white border-gray-200"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge
                                      className={`text-xs border ${
                                        item.historyStatus?.toUpperCase() ===
                                        "REJECTED"
                                          ? "bg-red-100 text-red-700 border-red-300"
                                          : item.historyStatus?.toUpperCase() ===
                                              "APPROVED"
                                            ? "bg-green-100 text-green-700 border-green-300"
                                            : "bg-amber-100 text-amber-700 border-amber-300"
                                      }`}
                                    >
                                      {item.historyStatus}
                                    </Badge>

                                    {item.actionAt && (
                                      <span className="text-xs text-gray-600">
                                        {
                                          formatUploadedDateTime(item.actionAt)
                                            .date
                                        }{" "}
                                        at{" "}
                                        {
                                          formatUploadedDateTime(item.actionAt)
                                            .time
                                        }
                                      </span>
                                    )}
                                  </div>

                                  {item.historyStatus?.toUpperCase() ===
                                    "REJECTED" &&
                                    item.remarks && (
                                      <p className="text-xs text-red-700 mt-1">
                                        <span className="font-medium">
                                          Remarks:
                                        </span>{" "}
                                        {item.remarks}
                                      </p>
                                    )}
                                </div>
                                {doc.uploadedUrl && (
                                  <button
                                    className="hover:bg-gray-100 rounded-lg p-1 transition-colors flex-shrink-0"
                                    style={{ color: "#5B9BD5" }}
                                    onClick={() =>
                                      window.open(doc.uploadedUrl, "_blank")
                                    }
                                  >
                                    <Eye className="w-4 h-4 cursor-pointer" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              Remove Document
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for removing this document. The applicant
              will be notified to upload a revised document.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deleteRemark" className="text-gray-900">
                Reason for Document Revision{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="deleteRemark"
                value={deleteRemark}
                onChange={(e) => setDeleteRemark(e.target.value)}
                placeholder="e.g., Document is not clear, incorrect document uploaded, signature missing..."
                className="min-h-[120px] bg-white border-gray-300 rounded-xl resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDocumentToDelete(null);
                setDeleteRemark("");
              }}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteDocument}
              disabled={isUploading}
              style={{ backgroundColor: "#5B9BD5" }}
              className="text-white rounded-xl"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  loading...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
